import { useEffect, useRef, useState } from 'react';

export type MapMarkerProps = {
  map?: google.maps.Map;
  position: google.maps.LatLngLiteral;
  iconUrl: string;
  iconSize?: number;
  draggable?: boolean;
  zIndex?: number;
  updateReactionPosition: (position: google.maps.LatLngLiteral) => void;
  isLocationInShape: (location: google.maps.LatLngLiteral) => boolean;
};

// AdvancedMarkerElement.position can read back as LatLng (methods) or a literal (number props).
const toLiteral = (position: google.maps.marker.AdvancedMarkerElement['position']): google.maps.LatLngLiteral => {
  if (!position) {
    return { lat: 0, lng: 0 };
  }
  const asLatLng = position as google.maps.LatLng;
  if (typeof asLatLng.lat === 'function') {
    return { lat: asLatLng.lat(), lng: asLatLng.lng() };
  }
  const literal = position as google.maps.LatLngLiteral;
  return { lat: literal.lat, lng: literal.lng };
};

const buildIcon = (iconUrl: string, iconSize: number): HTMLImageElement => {
  const img = document.createElement('img');
  img.src = iconUrl;
  img.style.width = `${iconSize}px`;
  img.style.height = `${iconSize}px`;
  // AdvancedMarkerElement anchors content bottom-center; shift down to center it on the point
  // (matches the legacy Marker anchor at icon center).
  img.style.transform = 'translateY(50%)';
  return img;
};

export const MapMarker = ({
  map,
  position,
  iconUrl,
  iconSize = 64,
  draggable = false,
  zIndex,
  updateReactionPosition,
  isLocationInShape,
}: MapMarkerProps): null => {
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement>();
  const lastPosition = useRef<google.maps.LatLngLiteral>();

  useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.marker.AdvancedMarkerElement());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.map = null;
      }
    };
  }, [marker]);

  useEffect(() => {
    if (marker) {
      marker.content = buildIcon(iconUrl, iconSize);
    }
  }, [marker, iconUrl, iconSize]);

  useEffect(() => {
    if (marker) {
      marker.position = position;
      marker.map = map ?? null;
      if (zIndex !== undefined) {
        marker.zIndex = zIndex;
      }
    }
  }, [marker, map, position, zIndex]);

  useEffect(() => {
    if (marker) {
      marker.gmpDraggable = draggable;
    }
  }, [marker, draggable]);

  useEffect(() => {
    if (marker) {
      google.maps.event.addListener(marker, 'dragend', () => {
        const { lat, lng } = toLiteral(marker.position);
        if (!isLocationInShape({ lat, lng }) || !draggable) {
          marker.position = lastPosition.current ?? null;
        } else {
          updateReactionPosition({ lat, lng });
        }
      });
      google.maps.event.addListener(marker, 'dragstart', () => {
        lastPosition.current = toLiteral(marker.position);
      });
    }
    return () => {
      if (marker) {
        google.maps.event.clearInstanceListeners(marker);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker, draggable]);

  return null;
};
